/**
 * Impersonation banner — always-visible warning strip shown to a superadmin while they view PRISM as a
 * customer user via the Admin Console.
 *
 * Exit first CLOSES the session server-side (POST /admin/impersonation/end, sent with the view-as token), so
 * the token stops working immediately (spec 2026-09-29-impersonation-read-only-and-audit §2.2, D3), then
 * restores the stashed superadmin session. /end is best-effort: a failure is logged and the operator still
 * gets out.
 */
import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { adminAPI, authAPI } from '../services/api';
import {
  ORIGINAL_TOKEN_KEY,
  ORIGINAL_USER_KEY,
  restoreImpersonator,
} from '../services/impersonationSession';

const ImpersonationBanner: React.FC = () => {
  const [exiting, setExiting] = useState(false);
  const originalToken = localStorage.getItem(ORIGINAL_TOKEN_KEY);
  const originalUserStr = localStorage.getItem(ORIGINAL_USER_KEY);
  const user = authAPI.getCurrentUser();

  if (!originalToken || !originalUserStr || !user) return null;

  const handleExit = async () => {
    if (exiting) return;
    setExiting(true);
    try {
      await adminAPI.endImpersonation();
    } catch (err) {
      console.warn('[impersonation] ending the view-as session failed; exiting anyway:', err);
    }
    restoreImpersonator();
  };

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2000,
        bgcolor: '#ffa500',
        color: '#1a0f00',
        py: 1,
        px: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
        borderBottom: '2px solid #cc7a00',
      }}
    >
      <Visibility fontSize="small" />
      <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
        Viewing as <strong>{user.email}</strong> — every action is audit-logged
      </Typography>
      <Button
        onClick={handleExit}
        disabled={exiting}
        size="small"
        variant="contained"
        sx={{
          bgcolor: '#1a0f00',
          color: '#ffa500',
          ml: 2,
          '&:hover': { bgcolor: '#000' },
        }}
      >
        Exit impersonation
      </Button>
    </Box>
  );
};

export default ImpersonationBanner;
